import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { apiClient } from '@/api/client';
import type { QuestionnaireCreatedDto } from '@/features/patient-record/api/questionnaires';
import { patientQueries } from '@/features/patient-record/queries';
import { mapQuestionnaire } from '@/features/patient-record/mappers/questionnaire';
import type {
  QuestionnaireDelivery,
  QuestionnaireRevealLink,
  QuestionnaireViewModel,
} from '@/features/patient-record/types';

export interface SendQuestionnaireInput {
  readonly templateId: string;
  readonly templateName: string;
  readonly deliveryMethod: QuestionnaireDelivery;
  readonly expiresAt?: string | null;
}

/** Create result: the new row plus the one-time reveal (URL delivery only; null otherwise). */
export interface CreatedQuestionnaire {
  readonly view: QuestionnaireViewModel;
  readonly reveal: QuestionnaireRevealLink | null;
}

interface MutationContext {
  readonly previous: QuestionnaireViewModel[] | undefined;
}

type SendResult = UseMutationResult<
  CreatedQuestionnaire,
  Error,
  SendQuestionnaireInput,
  MutationContext
>;

type RegenerateResult = UseMutationResult<QuestionnaireRevealLink, Error, string>;

type RevokeResult = UseMutationResult<undefined, Error, string, MutationContext>;

/** Pull the one-time reveal off a create/regenerate response, or null when absent (EMAIL/SMS). */
function extractReveal(
  dto: QuestionnaireCreatedDto,
  requestGuid: string,
): QuestionnaireRevealLink | null {
  const token = dto.access_token;
  const uri = dto.questionnaire_uri;
  if (typeof token !== 'string' || token.length === 0) return null;
  if (typeof uri !== 'string' || uri.length === 0) return null;
  return { requestGuid, accessToken: token, questionnaireUri: uri };
}

/**
 * Resource-level mutations for patient questionnaires. Today exposes
 * `send` and `revoke`; remind / extend land in the same hook when
 * those endpoints land.
 */
export function useQuestionnaireMutations(
  guid: string,
): {
  readonly send: SendResult;
  readonly regenerate: RegenerateResult;
  readonly revoke: RevokeResult;
  readonly markSent: RevokeResult;
} {
  const queryClient = useQueryClient();
  const { queryKey } = patientQueries.questionnaires(guid);

  const snapshot = (): MutationContext => ({
    previous: queryClient.getQueryData(queryKey),
  });

  const rollback = (ctx: MutationContext | undefined): void => {
    if (ctx === undefined) return;
    queryClient.setQueryData(queryKey, ctx.previous);
  };

  const settle = (): void => {
    queryClient.invalidateQueries({ queryKey }).catch(() => undefined);
    // Also refresh the cross-patient Browse Questionnaires table, which shows these same rows.
    queryClient.invalidateQueries({ queryKey: ['questionnaires', 'browse'] }).catch(() => undefined);
  };

  const send = useMutation<
    CreatedQuestionnaire,
    Error,
    SendQuestionnaireInput,
    MutationContext
  >({
    meta: { successMessage: 'success.toast.title.questionnaire_created' },
    mutationFn: async (input) => {
      const dto = await apiClient.sendQuestionnaire(guid, {
        template_id: input.templateId,
        delivery_method: input.deliveryMethod,
        expires_at: input.expiresAt ?? null,
      });
      const view = mapQuestionnaire(dto);
      return { view, reveal: extractReveal(dto, view.id) };
    },
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey });
      const ctx = snapshot();
      // URL is manual: the backend creates it NOT_SENT (no sentAt) until "mark as sent".
      const manual = input.deliveryMethod === 'URL';
      const optimistic: QuestionnaireViewModel = {
        id: `optimistic-${Date.now().toString(36)}`,
        templateId: input.templateId,
        templateName: input.templateName,
        status: manual ? 'NOT_SENT' : 'SENT',
        sentAt: manual ? null : new Date().toISOString(),
        expiresAt: input.expiresAt ?? null,
        completedAt: null,
        deliveryMethod: input.deliveryMethod,
        jobExecutionId: null,
      };
      queryClient.setQueryData(queryKey, [optimistic, ...(ctx.previous ?? [])]);
      return ctx;
    },
    onError: (_error, _input, ctx) => {
      rollback(ctx);
    },
    onSettled: settle,
  });

  const regenerate = useMutation<QuestionnaireRevealLink, Error, string>({
    meta: { successMessage: 'success.toast.title.questionnaire_link_regenerated' },
    mutationFn: async (id) => {
      const dto = await apiClient.regenerateQuestionnaireLink(guid, id);
      const reveal = extractReveal(dto, id);
      if (reveal === null) {
        throw new Error('Regenerate did not return a link.');
      }
      return reveal;
    },
    // The list rows don't display the token, but OPENED→ state and timestamps may shift server-side.
    onSettled: settle,
  });

  const revoke = useMutation<undefined, Error, string, MutationContext>({
    meta: { successMessage: 'success.toast.title.questionnaire_deleted' },
    mutationFn: (id) =>
      apiClient.updateQuestionnaire(guid, id, { status: 'REVOKED' }),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey });
      const ctx = snapshot();
      queryClient.setQueryData<QuestionnaireViewModel[]>(
        queryKey,
        (ctx.previous ?? []).map((q) =>
          q.id === id ? { ...q, status: 'REVOKED' } : q,
        ),
      );
      return ctx;
    },
    onError: (_e, _v, ctx) => {
      rollback(ctx);
    },
    onSettled: settle,
  });

  const markSent = useMutation<undefined, Error, string, MutationContext>({
    meta: { successMessage: 'success.toast.title.questionnaire_sent' },
    mutationFn: (id) => apiClient.updateQuestionnaire(guid, id, { status: 'SENT' }),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey });
      const ctx = snapshot();
      queryClient.setQueryData<QuestionnaireViewModel[]>(
        queryKey,
        (ctx.previous ?? []).map((q) =>
          q.id === id ? { ...q, status: 'SENT', sentAt: new Date().toISOString() } : q,
        ),
      );
      return ctx;
    },
    onError: (_e, _v, ctx) => {
      rollback(ctx);
    },
    onSettled: settle,
  });

  return { send, regenerate, revoke, markSent };
}
